import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

export const Card = styled.div`
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.lg};
  padding: ${theme.spacing.md};

  ${media.sm} {
    padding: ${theme.spacing.sm};
    border-radius: ${theme.radius.md};
  }
`;

export const PageContainer = styled.div`
  padding: ${theme.spacing.md};

  ${media.sm} {
    padding: ${theme.spacing.sm};
  }
`;

export const PageTitle = styled.h1`
  font-size: ${theme.fontSize.xl};
  font-weight: 700;
  margin-bottom: ${theme.spacing.md};

  ${media.sm} {
    font-size: ${theme.fontSize.lg};
    margin-bottom: ${theme.spacing.sm};
  }
`;

export const SectionTitle = styled.h2`
  font-size: ${theme.fontSize.lg};
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};
`;

export const Grid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.columns ?? 3}, 1fr);
  gap: ${theme.spacing.md};

  ${media.lg} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${media.sm} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.sm};
  }
`;

export const StatCard = styled(Card)`
  text-align: center;
  padding: ${theme.spacing.md};

  ${media.sm} {
    padding: ${theme.spacing.sm};
  }
`;

export const StatValue = styled.div`
  font-size: ${theme.fontSize.xl};
  font-weight: 700;
  color: ${theme.colors.primary};

  ${media.sm} {
    font-size: ${theme.fontSize.lg};
  }
`;

export const StatLabel = styled.div`
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};

  ${media.sm} {
    font-size: ${theme.fontSize.xs};
    margin-top: 2px;
  }
`;

export const StatsRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: ${theme.spacing.xs};
  }
`;

/*
 * The 76px basis is what decides how many cards share a row, and it is set just under what four of
 * them need on a 360px phone (4 × 76 + 12 of gap = 316, against 344 of page). So the usual phone
 * keeps all four across, and only a 320px screen — where the fourth card would squeeze "$1,927.49"
 * out over its neighbour — drops to two rows of two.
 */
export const CompactStatCard = styled(Card)`
  flex: 1 1 76px;
  padding: ${theme.spacing.md};
  min-width: 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};

    ${StatValue} {
      font-size: ${theme.fontSize.sm};
    }
    ${StatLabel} {
      font-size: 10px;
    }
  }
`;

export const SubStat = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  font-family: monospace;
  /* Four cards across a phone leave ~65px each, and "~$0.000e+0/mana" is one unbreakable 99px token
     that would otherwise spill out of the card and be clipped by the page. Let it take two lines. */
  overflow-wrap: anywhere;
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* Panning a table to its last column must not hand the leftover swipe to the browser, which reads
     it as a back gesture and navigates away mid-read. */
  overscroll-behavior-x: contain;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;

  th,
  td {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${theme.colors.border};
    font-size: ${theme.fontSize.sm};
    white-space: nowrap;
  }

  th {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: ${theme.fontSize.xs};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tr:hover td {
    background: ${theme.colors.bgHover};
  }

  ${media.sm} {
    th,
    td {
      padding: ${theme.spacing.xs} ${theme.spacing.sm};
      font-size: ${theme.fontSize.xs};
    }
  }
`;

export const Badge = styled.span<{ color?: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.fontSize.xs};
  font-weight: 600;
  background: ${(p) => p.color ?? theme.colors.primary}22;
  color: ${(p) => p.color ?? theme.colors.primary};
`;

export const Button = styled.button<{
  variant?: "primary" | "ghost" | "danger";
}>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.radius.md};
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;

  ${(p) => {
    switch (p.variant) {
      case "danger":
        return `
          background: ${theme.colors.danger}22;
          color: ${theme.colors.danger};
          &:hover { background: ${theme.colors.danger}44; }
        `;
      case "ghost":
        return `
          background: transparent;
          color: ${theme.colors.textMuted};
          &:hover { background: ${theme.colors.bgHover}; color: ${theme.colors.text}; }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover { background: ${theme.colors.primaryHover}; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
  font-size: ${theme.fontSize.sm};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

export const Select = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.radius.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
  font-size: ${theme.fontSize.sm};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

export const Mono = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: ${theme.fontSize.xs};
`;

export const Truncate = styled(Mono)`
  max-width: 180px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;

  ${media.sm} {
    max-width: 100px;
  }
`;

export const Flex = styled.div<{
  gap?: string;
  align?: string;
  justify?: string;
  wrap?: boolean;
}>`
  display: flex;
  gap: ${(p) => p.gap ?? theme.spacing.md};
  align-items: ${(p) => p.align ?? "center"};
  justify-content: ${(p) => p.justify ?? "flex-start"};
  flex-wrap: ${(p) => (p.wrap ? "wrap" : "nowrap")};
`;

export const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Loading = () => (
  <Flex justify="center" style={{ padding: "48px" }}>
    <Spinner />
  </Flex>
);

export const SortableHeader = styled.th<{ $active?: boolean }>`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  color: ${(p) => (p.$active ? theme.colors.primary : "inherit")} !important;
  &:hover {
    color: ${theme.colors.primary} !important;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

export const ChartTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${theme.fontSize.md};
  font-weight: 600;
  color: ${theme.colors.text};
`;

export const ChartCard = styled(Card)`
  padding: ${theme.spacing.md};

  ${media.md} {
    padding: ${theme.spacing.sm};
  }
`;
